// Underline for the desktop header menu.
// - Neighbouring items: the underline glides across.
// - Distant items: the old underline shrinks away while the new one grows in
//   place, so it never streaks across the whole menu.
// Transform-only animation (compositor); purely decorative, never intercepts
// pointer events or changes the menu's state. Also runs in the theme editor so
// the preview matches the storefront; editor re-renders of the header are
// picked up by the MutationObserver below.
(() => {
  const LEAVE_DELAY = 120;
  let menu;
  let bar;
  let target; // title the bar currently marks
  let pinned; // title of a menu link that was clicked (navigation in flight)
  let leaveTimer;

  const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)');
  const itemOf = (el) => el?.closest('.menu-list__list-item');
  const titleOf = (el) => itemOf(el)?.querySelector('.menu-list__link-title');
  const visibleItems = () =>
    [...(menu?.querySelectorAll('.menu-list__list-item:not([slot])') ?? [])].filter(
      (item) => item.getAttribute('aria-hidden') !== 'true' && item.offsetWidth > 0
    );

  function currentTitle() {
    if (pinned?.isConnected) return pinned;
    const link = menu?.querySelector(
      '.menu-list__list-item:not([slot]) .menu-list__link:is([aria-current="page"], .menu-list__link--active)'
    );
    return titleOf(link);
  }

  function ensureBar() {
    const found = document.querySelector('header-menu');
    if (!found) return false;
    if (found !== menu) {
      menu = found;
      bind();
    }
    if (!bar || !bar.isConnected) {
      bar = document.createElement('span');
      bar.className = 'vi-nav-indicator';
      bar.setAttribute('aria-hidden', 'true');
      menu.append(bar);
      target = undefined;
    }
    return true;
  }

  // Geometry is read fresh every time, relative to the bar's own positioning
  // context, so zoom, resizes and header reflows can never leave it stale.
  function geometry(title) {
    const container = bar.offsetParent;
    const box = title?.getBoundingClientRect();
    const item = itemOf(title);
    if (!title || !container || !box || box.width === 0 || item?.getAttribute('aria-hidden') === 'true') return null;
    const style = getComputedStyle(title);
    const padStart = parseFloat(style.paddingInlineStart) || 0;
    const padEnd = parseFloat(style.paddingInlineEnd) || 0;
    const origin = container.getBoundingClientRect();
    return {
      x: box.left - origin.left + container.scrollLeft + padStart,
      y: box.bottom - origin.top + container.scrollTop + 6,
      w: Math.max(0, box.width - padStart - padEnd),
    };
  }

  const place = (el, { x, y, w }, scale = w) => {
    el.style.transform = `translate3d(${x}px, ${y}px, 0) scaleX(${scale})`;
  };

  // Set a transform without animating, then re-enable transitions.
  function jump(el, g, scale) {
    el.classList.add('no-slide');
    place(el, g, scale);
    void el.offsetWidth;
    el.classList.remove('no-slide');
  }

  function adjacent(a, b) {
    const items = visibleItems();
    const i = items.indexOf(itemOf(a));
    const j = items.indexOf(itemOf(b));
    return i !== -1 && j !== -1 && Math.abs(i - j) <= 1;
  }

  // The old underline collapses toward its centre and fades.
  function leaveGhost(g) {
    if (!g || reduceMotion.matches) return;
    const ghost = bar.cloneNode();
    ghost.classList.remove('is-visible');
    ghost.classList.add('is-ghost');
    jump(ghost, g);
    ghost.style.opacity = '1';
    bar.before(ghost);
    requestAnimationFrame(() => {
      ghost.style.transform = `translate3d(${g.x + g.w / 2}px, ${g.y}px, 0) scaleX(0.001)`;
      ghost.style.opacity = '0';
    });
    setTimeout(() => ghost.remove(), 400);
  }

  function moveTo(title, { instant = false } = {}) {
    if (!ensureBar()) return;
    const g = geometry(title);
    if (!g) {
      bar.classList.remove('is-visible');
      target = undefined;
      return;
    }

    const visible = bar.classList.contains('is-visible');
    if (instant || (visible && title === target)) {
      jump(bar, g);
    } else if (!visible || reduceMotion.matches) {
      // First appearance: grow out from the label's centre.
      jump(bar, { ...g, x: g.x + g.w / 2 }, 0.001);
      place(bar, g);
    } else if (target?.isConnected && adjacent(target, title)) {
      place(bar, g); // short glide
    } else {
      leaveGhost(target?.isConnected ? geometry(target) : null);
      jump(bar, { ...g, x: g.x + g.w / 2 }, 0.001);
      place(bar, g);
    }
    bar.classList.add('is-visible');
    target = title;
  }

  const settle = () => moveTo(currentTitle());

  function onEnter(event) {
    const item = event.target instanceof Element ? itemOf(event.target) : null;
    if (!item || item.slot) return; // overflow / "More" items keep Horizon's styling
    clearTimeout(leaveTimer);
    const title = titleOf(item);
    if (title && title !== target) moveTo(title);
  }

  function onLeave() {
    clearTimeout(leaveTimer);
    leaveTimer = setTimeout(settle, LEAVE_DELAY);
  }

  // A clicked menu link stays marked while the next page loads instead of
  // sliding back to the page being left.
  function onClick(event) {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey) return;
    const link = event.target instanceof Element ? event.target.closest('a.menu-list__link[href]') : null;
    const item = itemOf(link);
    if (!link || !item || item.slot) return;
    pinned = titleOf(item);
    clearTimeout(leaveTimer);
    if (pinned !== target) moveTo(pinned);
  }

  // Re-measure on the next frame and once more after Horizon's overflow list
  // has redistributed items (it reacts to the same resize).
  let frame;
  let lateTimer;
  function remeasure() {
    cancelAnimationFrame(frame);
    clearTimeout(lateTimer);
    const run = () => ensureBar() && moveTo(target?.isConnected ? target : currentTitle(), { instant: true });
    frame = requestAnimationFrame(run);
    lateTimer = setTimeout(run, 250);
  }

  const resizeObserver = new ResizeObserver(remeasure);

  function bind() {
    resizeObserver.disconnect();
    resizeObserver.observe(menu);
    const header = menu.closest('header-component, .header');
    if (header) resizeObserver.observe(header);
    menu.addEventListener('pointerover', onEnter);
    menu.addEventListener('focusin', onEnter);
    menu.addEventListener('pointerleave', onLeave);
    menu.addEventListener('click', onClick);
    menu.addEventListener('focusout', (event) => {
      if (!(event.relatedTarget instanceof Node) || !menu.contains(event.relatedTarget)) onLeave();
    });
  }

  function init() {
    if (!ensureBar()) return;
    moveTo(currentTitle(), { instant: true });
    // Horizon re-renders (hydrates/morphs) the header after load, which drops
    // the bar or replaces header-menu (or the whole header). Watch the body so a
    // replaced header is still seen; the check itself is two cheap lookups.
    const header = document.body;
    let queued = false;
    new MutationObserver(() => {
      if (queued || (bar?.isConnected && document.querySelector('header-menu') === menu)) return;
      queued = true;
      setTimeout(() => {
        queued = false;
        if (ensureBar()) moveTo(target?.isConnected ? target : currentTitle(), { instant: true });
      });
    }).observe(header, { childList: true, subtree: true });
    document.fonts?.ready.then(remeasure);
    // Browser zoom and window resizes move the menu without always resizing it.
    window.addEventListener('resize', remeasure, { passive: true });
    window.visualViewport?.addEventListener('resize', remeasure, { passive: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
  // Back/forward cache: the active page may differ from what was last shown.
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    pinned = undefined;
    if (ensureBar()) moveTo(currentTitle(), { instant: true });
  });
})();
